import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Loader2,
  LogIn,
  Mail,
  MapPin,
  Phone,
  Plus,
  Router as RouterIcon,
  Server,
  Shield,
  Sparkles,
  User,
  Users as UsersIcon,
  Wifi,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { API_CONFIG, fetchApp, fetchData } from "../../services/apiService";

interface Role {
  id: string;
  name: string;
}

interface UserDetail {
  id: string;
  username: string;
  email: string;
  client_id: string;
  role_id: string;
  role?: Role;
}

interface Secret {
  id: string;
  name: string;
  username: string;
  password: string;
  created_at: string;
}

interface Router {
  id: string;
  name: string;
  ip_address: string;
  password: string;
  client_id: string;
  created_at: string;
  updated_at: string;
  secrets: Secret[];
}

interface ClientDetail {
  id: string;
  name: string;
  contact_email: string;
  contact_phone?: string;
  address?: string;
  users: UserDetail[];
  routers: Router[];
}

interface ClientDetailPageProps {
  clientId: string | number;
  onBack: () => void;
}

interface AddUserForm {
  username: string;
  email: string;
  password: string;
  role_id: string;
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value?: number | null) {
  return new Intl.NumberFormat("en-KE").format(Number(value || 0));
}

function getInitials(name?: string) {
  if (!name) return "C";

  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return initials || "C";
}

function hasValue(value?: string | null) {
  return Boolean(value && value.trim());
}

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-KE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Africa/Nairobi",
  }).format(date);
}

function normalizeErrorMessage(error: any, fallback: string) {
  const message = error?.detail || error?.message || fallback;

  if (typeof message === "string") return message;

  if (Array.isArray(message)) {
    return message
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join(", ");
  }

  try {
    return JSON.stringify(message);
  } catch {
    return fallback;
  }
}

function getRoleBadgeColor(roleName?: string) {
  const normalized = String(roleName || "").toLowerCase();

  if (normalized.includes("admin")) {
    return "border-purple-100 bg-purple-50 text-purple-700";
  }

  if (normalized.includes("manager")) {
    return "border-emerald-100 bg-emerald-50 text-emerald-700";
  }

  if (normalized.includes("viewer")) {
    return "border-slate-200 bg-slate-50 text-slate-600";
  }

  return "border-blue-100 bg-blue-50 text-blue-700";
}

function StatCard({
  title,
  value,
  helper,
  icon,
  tone,
}: {
  title: string;
  value: number;
  helper: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div
        className={cn(
          "absolute right-[-34px] top-[-34px] h-24 w-24 rounded-full opacity-15 blur-2xl",
          tone,
        )}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>

          <h3 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
            {formatNumber(value)}
          </h3>

          <p className="mt-1 text-xs font-medium text-slate-400">{helper}</p>
        </div>

        <div
          className={cn(
            "rounded-2xl p-3 text-white shadow-lg transition group-hover:scale-105",
            tone,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function MiniMetric({
  title,
  value,
  icon,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-white/15 p-2 text-white">{icon}</div>

        <div>
          <p className="text-xs font-medium text-white/60">{title}</p>
          <p className="text-xl font-black text-white">{formatNumber(value)}</p>
        </div>
      </div>
    </div>
  );
}

function ContactBlock({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  tone: string;
}) {
  return (
    <div className="rounded-3xl border border-slate-100 bg-white/90 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={cn("rounded-2xl p-3", tone)}>{icon}</div>

        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-wide text-slate-400">
            {label}
          </p>

          <p
            className={cn(
              "mt-1 break-words text-sm font-bold",
              hasValue(value) ? "text-slate-900" : "text-slate-400",
            )}
          >
            {hasValue(value) ? value : "Not set"}
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-14 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-white text-slate-400 shadow-sm">
        {icon}
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">{title}</h3>

      <p className="mx-auto mt-1 max-w-md text-sm font-medium leading-6 text-slate-500">
        {description}
      </p>

      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

function CopyButton({
  value,
  copyKey,
  copiedStates,
  onCopy,
}: {
  value?: string | null;
  copyKey: string;
  copiedStates: Record<string, boolean>;
  onCopy: (text: string, key: string) => void;
}) {
  if (!value) return null;

  return (
    <button
      type="button"
      onClick={() => onCopy(value, copyKey)}
      className="rounded-xl p-1.5 text-slate-400 transition hover:bg-white hover:text-slate-700"
      title="Copy"
    >
      {copiedStates[copyKey] ? (
        <Check className="h-4 w-4 text-emerald-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
}

function DetailPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{icon}</span>

        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
            {label}
          </p>

          <p className="truncate text-sm font-bold text-slate-800">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

const ClientDetailPage: React.FC<ClientDetailPageProps> = ({
  clientId,
  onBack,
}) => {
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  const [roles, setRoles] = useState<Role[]>([]);

  const [impersonateDialog, setImpersonateDialog] = useState<{
    open: boolean;
    user: { id: string; username: string } | null;
  }>({
    open: false,
    user: null,
  });

  const [isImpersonating, setIsImpersonating] = useState(false);

  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>(
    {},
  );

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>(
    {},
  );
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);

  const [userForm, setUserForm] = useState<AddUserForm>({
    username: "",
    email: "",
    password: "",
    role_id: "",
  });

  const [showAddRouterModal, setShowAddRouterModal] = useState(false);
  const [routerSubmitting, setRouterSubmitting] = useState(false);
  const [routerError, setRouterError] = useState<string | null>(null);

  const [routerForm, setRouterForm] = useState({
    name: "",
    mikrotik_username: "admin",
    mikrotik_password: "",
    dns: "1.1.1.1",
  });

  useEffect(() => {
    loadClientDetail();
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadClientDetail = async () => {
    setLoading(true);
    setPageError(null);

    try {
      const data = await fetchData(`${API_CONFIG.baseUrl}/clients/${clientId}`);
      setClient(data);
    } catch (error) {
      console.error("Error loading client:", error);
      setPageError(
        "Failed to load client details. Please refresh and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await fetchData(`${API_CONFIG.baseUrl}/roles/`);

      const safeRoles = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
            ? data.data
            : [];

      setRoles(safeRoles);
    } catch (error) {
      console.error("Error loading roles:", error);
      setRoles([]);
    }
  };

  const totalSecrets = useMemo(() => {
    if (!client) return 0;

    return client.routers.reduce(
      (sum, router) => sum + (router.secrets?.length || 0),
      0,
    );
  }, [client]);

  const initiateImpersonation = (user: { id: string; username: string }) => {
    setImpersonateDialog({
      open: true,
      user,
    });
  };

  const handleImpersonateConfirm = async () => {
    const targetUser = impersonateDialog.user;

    if (!targetUser) return;

    setIsImpersonating(true);

    try {
      const response = await fetchApp(
        `${API_CONFIG.baseUrl}/auth/impersonate`,
        {
          method: "POST",
          body: JSON.stringify({ user_id: targetUser.id }),
        },
      );

      if (response && response.access_token) {
        localStorage.setItem("token", response.access_token);

        if (response.role) {
          localStorage.setItem("role", response.role);
        }

        window.location.href = "/overview";
      } else {
        throw new Error("No access token received");
      }
    } catch (error: any) {
      console.error("Impersonation failed", error);

      alert(
        normalizeErrorMessage(
          error,
          "Failed to impersonate user. Please check permissions.",
        ),
      );

      setIsImpersonating(false);
      setImpersonateDialog({ open: false, user: null });
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);

      setCopiedStates((prev) => ({ ...prev, [key]: true }));

      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const openAddUserModal = () => {
    setUserError(null);
    setUserFormErrors({});
    setShowNewUserPassword(false);

    setUserForm({
      username: "",
      email: "",
      password: "",
      role_id: roles[0]?.id || "",
    });

    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    if (userSubmitting) return;

    setShowAddUserModal(false);
    setUserError(null);
    setUserFormErrors({});
    setShowNewUserPassword(false);

    setUserForm({
      username: "",
      email: "",
      password: "",
      role_id: "",
    });
  };

  const validateAddUserForm = () => {
    const errors: Record<string, string> = {};

    if (!userForm.username.trim()) {
      errors.username = "Username is required.";
    }

    if (!userForm.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(userForm.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!userForm.password.trim()) {
      errors.password = "Password is required.";
    }

    if (!userForm.role_id) {
      errors.role_id = "Role is required.";
    }

    setUserFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const submitAddUser = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!client) return;

    setUserError(null);

    if (!validateAddUserForm()) return;

    try {
      setUserSubmitting(true);

      const payload = {
        username: userForm.username.trim(),
        email: userForm.email.trim(),
        password: userForm.password.trim(),
        client_id: client.id,
        role_id: userForm.role_id,
      };

      await fetchApp(`${API_CONFIG.baseUrl}/users/`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      closeAddUserModal();
      await loadClientDetail();
    } catch (error: any) {
      console.error("Error creating user:", error);

      setUserError(
        normalizeErrorMessage(
          error,
          "Failed to create user. Please check the details and try again.",
        ),
      );
    } finally {
      setUserSubmitting(false);
    }
  };

  const openAddRouterModal = () => {
    setRouterError(null);
    setRouterForm({
      name: "",
      mikrotik_username: "admin",
      mikrotik_password: "",
      dns: "1.1.1.1",
    });
    setShowAddRouterModal(true);
  };

  const closeAddRouterModal = () => {
    if (routerSubmitting) return;

    setShowAddRouterModal(false);
    setRouterError(null);
  };

  const validateRouterForm = () => {
    const name = routerForm.name.trim();
    const user = routerForm.mikrotik_username.trim();
    const pass = routerForm.mikrotik_password.trim();

    if (!name) return "Router name is required.";
    if (!user) return "MikroTik username is required.";
    if (!pass) return "MikroTik password is required.";

    return null;
  };

  const submitAddRouter = async (event: React.FormEvent) => {
    event.preventDefault();
    setRouterError(null);

    const err = validateRouterForm();

    if (err) {
      setRouterError(err);
      return;
    }

    try {
      setRouterSubmitting(true);

      const payload = {
        client_id: clientId,
        name: routerForm.name.trim(),
        mikrotik_username: routerForm.mikrotik_username.trim(),
        mikrotik_password: routerForm.mikrotik_password.trim(),
        dns: routerForm.dns.trim() || "1.1.1.1",
      };

      await fetchApp(`${API_CONFIG.baseUrl}/routers/`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      closeAddRouterModal();
      await loadClientDetail();
    } catch (error: any) {
      console.error(error);

      setRouterError(
        normalizeErrorMessage(
          error,
          "Failed to add router. Please verify details and try again.",
        ),
      );
    } finally {
      setRouterSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
        <div className="flex min-h-[60vh] items-center justify-center rounded-3xl border border-white bg-white/95 shadow-sm">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />

            <p className="mt-3 text-sm font-black text-slate-600">
              Loading client details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
        <div className="rounded-3xl border border-white bg-white/95 px-4 py-16 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
            <User className="h-8 w-8" />
          </div>

          <h3 className="mt-4 text-xl font-black text-slate-950">
            Client Not Found
          </h3>

          <p className="mx-auto mt-2 max-w-md text-sm font-medium leading-6 text-slate-500">
            {pageError || "The requested client could not be found."}
          </p>

          <button
            onClick={onBack}
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
      <div className="w-full max-w-none space-y-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 rounded-2xl border border-white bg-white/80 px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-white hover:text-slate-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to clients
        </button>

        {pageError && (
          <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {pageError}
          </div>
        )}

        <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl md:p-6">
          <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute bottom-[-100px] left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-5 xl:flex-row xl:items-center">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/80 backdrop-blur">
                <Sparkles size={14} />
                Client Detail Center
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-white text-2xl font-black text-slate-950 shadow-xl">
                  {getInitials(client.name)}
                </div>

                <div className="min-w-0">
                  <h1 className="break-words text-3xl font-black tracking-tight md:text-4xl">
                    {client.name}
                  </h1>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                    View client users, routers, credentials, contact details,
                    and super admin access actions from one clean workspace.
                  </p>

                  <p className="mt-2 max-w-full truncate text-xs font-semibold text-white/40">
                    Client ID: <span className="font-mono">{client.id}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[480px]">
              <MiniMetric
                title="Users"
                value={client.users.length}
                icon={<UsersIcon size={18} />}
              />

              <MiniMetric
                title="Routers"
                value={client.routers.length}
                icon={<RouterIcon size={18} />}
              />

              <MiniMetric
                title="PPP Secrets"
                value={totalSecrets}
                icon={<Shield size={18} />}
              />
            </div>
          </div>

          <div className="relative mt-6 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-medium text-white/60">
              Add users and MikroTik router records for this client.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                onClick={openAddUserModal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-2.5 text-sm font-black text-slate-950 shadow-sm transition hover:bg-slate-100"
              >
                <Plus className="h-4 w-4" />
                Add User
              </button>

              <button
                onClick={openAddRouterModal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Router
              </button>
            </div>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Users"
            value={client.users.length}
            helper="Application access accounts"
            icon={<UsersIcon size={23} />}
            tone="bg-emerald-600"
          />

          <StatCard
            title="Total Routers"
            value={client.routers.length}
            helper="MikroTik devices configured"
            icon={<Wifi size={23} />}
            tone="bg-blue-600"
          />

          <StatCard
            title="PPP Secrets"
            value={totalSecrets}
            helper="Linked router credentials"
            icon={<Shield size={23} />}
            tone="bg-violet-600"
          />

          <StatCard
            title="Contact Status"
            value={
              [
                client.contact_email,
                client.contact_phone,
                client.address,
              ].filter(hasValue).length
            }
            helper="Profile fields completed"
            icon={<Building2 size={23} />}
            tone="bg-amber-500"
          />
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <ContactBlock
            icon={<Mail className="h-5 w-5" />}
            label="Email Address"
            value={client.contact_email}
            tone="bg-blue-50 text-blue-700"
          />

          <ContactBlock
            icon={<Phone className="h-5 w-5" />}
            label="Phone Number"
            value={client.contact_phone}
            tone="bg-emerald-50 text-emerald-700"
          />

          <ContactBlock
            icon={<MapPin className="h-5 w-5" />}
            label="Address"
            value={client.address}
            tone="bg-violet-50 text-violet-700"
          />
        </section>

        <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                <UsersIcon size={20} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">Application Users</h2>

                <p className="text-xs font-medium text-slate-400">
                  {formatNumber(client.users.length)} user
                  {client.users.length === 1 ? "" : "s"} with access
                </p>
              </div>
            </div>

            <button
              onClick={openAddUserModal}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add User
            </button>
          </div>

          {client.users.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<UsersIcon size={28} />}
                title="No Users"
                description="No application users are associated with this client yet."
                action={
                  <button
                    onClick={openAddUserModal}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
                  >
                    <Plus className="h-4 w-4" />
                    Add first user
                  </button>
                }
              />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="w-full min-w-[900px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-black uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-4">User</th>
                      <th className="px-5 py-4">Email</th>
                      <th className="px-5 py-4">Role</th>
                      <th className="px-5 py-4">User ID</th>
                      <th className="px-5 py-4 text-right">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {client.users.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-slate-100 transition hover:bg-slate-50/80"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-sm">
                              {getInitials(user.username)}
                            </div>

                            <div className="min-w-0">
                              <p className="max-w-[260px] truncate text-sm font-black text-slate-950">
                                {user.username}
                              </p>

                              <p className="mt-1 text-xs font-medium text-slate-400">
                                Application user
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="max-w-[280px] truncate">
                              {user.email}
                            </span>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black",
                              getRoleBadgeColor(user.role?.name),
                            )}
                          >
                            <Shield className="h-3.5 w-3.5" />
                            {user.role?.name || "User"}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <p className="max-w-[220px] truncate font-mono text-xs font-semibold text-slate-500">
                            {user.id}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={() => initiateImpersonation(user)}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
                          >
                            <LogIn className="h-4 w-4" />
                            Login as
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 lg:hidden">
                {client.users.map((user) => (
                  <div
                    key={user.id}
                    className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white shadow-sm">
                        {getInitials(user.username)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-black text-slate-950">
                          {user.username}
                        </h3>

                        <p className="mt-1 truncate text-xs font-semibold text-slate-400">
                          {user.email}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black",
                              getRoleBadgeColor(user.role?.name),
                            )}
                          >
                            <Shield className="h-3.5 w-3.5" />
                            {user.role?.name || "User"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => initiateImpersonation(user)}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
                    >
                      <LogIn className="h-4 w-4" />
                      Login as this user
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                <Wifi size={20} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">MikroTik Routers</h2>

                <p className="text-xs font-medium text-slate-400">
                  {formatNumber(client.routers.length)} router
                  {client.routers.length === 1 ? "" : "s"} configured
                </p>
              </div>
            </div>

            <button
              onClick={openAddRouterModal}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Router
            </button>
          </div>

          {client.routers.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<Wifi size={28} />}
                title="No Routers"
                description="No MikroTik routers are assigned to this client yet."
                action={
                  <button
                    onClick={openAddRouterModal}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-slate-800"
                  >
                    <Plus className="h-4 w-4" />
                    Add first router
                  </button>
                }
              />
            </div>
          ) : (
            <div className="grid gap-4 p-4 xl:grid-cols-2">
              {client.routers.map((router) => (
                <div
                  key={router.id}
                  className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
                >
                  <div className="border-b border-slate-100 bg-slate-50/80 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-sm">
                        <Server className="h-6 w-6" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-black text-slate-950">
                          {router.name}
                        </h3>

                        <p className="mt-1 truncate font-mono text-xs font-semibold text-slate-400">
                          {router.id}
                        </p>
                      </div>

                      <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                        {router.secrets?.length || 0} secrets
                      </span>
                    </div>
                  </div>

                  <div className="grid gap-3 p-4">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                        IP Address
                      </p>

                      <div className="mt-1 flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-mono text-sm font-black text-slate-900">
                          {router.ip_address || "—"}
                        </span>

                        <CopyButton
                          value={router.ip_address}
                          copyKey={`router-ip-${router.id}`}
                          copiedStates={copiedStates}
                          onCopy={copyToClipboard}
                        />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                        Password
                      </p>

                      <div className="mt-1 flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-mono text-sm font-black text-slate-900">
                          {showPasswords[`router-${router.id}`]
                            ? router.password
                            : "••••••••"}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            togglePasswordVisibility(`router-${router.id}`)
                          }
                          className="rounded-xl p-1.5 text-slate-400 transition hover:bg-white hover:text-slate-700"
                          title="Toggle password"
                        >
                          {showPasswords[`router-${router.id}`] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>

                        <CopyButton
                          value={router.password}
                          copyKey={`router-pass-${router.id}`}
                          copiedStates={copiedStates}
                          onCopy={copyToClipboard}
                        />
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <DetailPill
                        icon={<Calendar className="h-4 w-4" />}
                        label="Created"
                        value={formatDate(router.created_at)}
                      />

                      <DetailPill
                        icon={<Clock className="h-4 w-4" />}
                        label="Updated"
                        value={formatDate(router.updated_at)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={impersonateDialog.open}
        title="Confirm Impersonation"
        description={`Are you sure you want to log in as "${
          impersonateDialog.user?.username || "this user"
        }"? You will be logged out of your current super admin session immediately.`}
        confirmLabel="Yes, Login"
        variant="danger"
        loading={isImpersonating}
        onCancel={() => {
          if (!isImpersonating) {
            setImpersonateDialog({ open: false, user: null });
          }
        }}
        onConfirm={handleImpersonateConfirm}
      />

      {showAddUserModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white">
              <div className="absolute right-[-70px] top-[-70px] h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-white p-3 text-slate-950 shadow-lg">
                    <UsersIcon size={22} />
                  </div>

                  <div>
                    <h2 className="text-xl font-black">Add Client User</h2>

                    <p className="mt-1 text-sm leading-5 text-white/60">
                      Create a new application user under{" "}
                      <span className="font-bold text-white">
                        {client.name}
                      </span>
                      .
                    </p>
                  </div>
                </div>

                <button
                  onClick={closeAddUserModal}
                  type="button"
                  disabled={userSubmitting}
                  className="rounded-2xl bg-white/10 p-2 text-white/70 transition hover:bg-white/15 hover:text-white disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {userError && (
              <div className="px-5 pt-5">
                <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  {userError}
                </div>
              </div>
            )}

            <form
              onSubmit={submitAddUser}
              className="max-h-[calc(92vh-112px)] overflow-y-auto px-5 py-5"
            >
              <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-800">
                Client: <span className="font-black">{client.name}</span>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Username <span className="text-rose-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={userForm.username}
                    onChange={(event) =>
                      setUserForm((prev) => ({
                        ...prev,
                        username: event.target.value,
                      }))
                    }
                    className={cn(
                      "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                      userFormErrors.username
                        ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                        : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                    )}
                    placeholder="johndoe"
                  />

                  {userFormErrors.username && (
                    <p className="mt-1.5 text-xs font-bold text-rose-600">
                      {userFormErrors.username}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Email <span className="text-rose-500">*</span>
                  </label>

                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(event) =>
                      setUserForm((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                    className={cn(
                      "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                      userFormErrors.email
                        ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                        : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                    )}
                    placeholder="user@example.com"
                  />

                  {userFormErrors.email && (
                    <p className="mt-1.5 text-xs font-bold text-rose-600">
                      {userFormErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Password <span className="text-rose-500">*</span>
                  </label>

                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                    <input
                      type={showNewUserPassword ? "text" : "password"}
                      value={userForm.password}
                      onChange={(event) =>
                        setUserForm((prev) => ({
                          ...prev,
                          password: event.target.value,
                        }))
                      }
                      className={cn(
                        "w-full rounded-2xl border bg-slate-50 px-4 py-3 pl-10 pr-12 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                        userFormErrors.password
                          ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                          : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                      )}
                      placeholder="••••••••"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowNewUserPassword((previous) => !previous)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
                    >
                      {showNewUserPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {userFormErrors.password && (
                    <p className="mt-1.5 text-xs font-bold text-rose-600">
                      {userFormErrors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Role <span className="text-rose-500">*</span>
                  </label>

                  <select
                    value={userForm.role_id}
                    onChange={(event) =>
                      setUserForm((prev) => ({
                        ...prev,
                        role_id: event.target.value,
                      }))
                    }
                    className={cn(
                      "w-full rounded-2xl border bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:bg-white focus:ring-4",
                      userFormErrors.role_id
                        ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                        : "border-slate-200 focus:border-blue-400 focus:ring-blue-100",
                    )}
                  >
                    <option value="">Select role</option>

                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>

                  {userFormErrors.role_id && (
                    <p className="mt-1.5 text-xs font-bold text-rose-600">
                      {userFormErrors.role_id}
                    </p>
                  )}

                  {roles.length === 0 && (
                    <p className="mt-1.5 text-xs font-bold text-amber-600">
                      No roles found from /roles.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={closeAddUserModal}
                  disabled={userSubmitting}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={userSubmitting || roles.length === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {userSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddRouterModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white">
              <div className="absolute right-[-70px] top-[-70px] h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-white p-3 text-slate-950 shadow-lg">
                    <Wifi size={22} />
                  </div>

                  <div>
                    <h2 className="text-xl font-black">Add Router</h2>

                    <p className="mt-1 text-sm leading-5 text-white/60">
                      Create a new MikroTik router record for{" "}
                      <span className="font-bold text-white">
                        {client.name}
                      </span>
                      .
                    </p>
                  </div>
                </div>

                <button
                  onClick={closeAddRouterModal}
                  type="button"
                  disabled={routerSubmitting}
                  className="rounded-2xl bg-white/10 p-2 text-white/70 transition hover:bg-white/15 hover:text-white disabled:opacity-50"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {routerError && (
              <div className="px-5 pt-5">
                <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  {routerError}
                </div>
              </div>
            )}

            <form
              onSubmit={submitAddRouter}
              className="max-h-[calc(92vh-112px)] overflow-y-auto px-5 py-5"
            >
              <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-800">
                Client: <span className="font-black">{client.name}</span>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    Router Name <span className="text-rose-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={routerForm.name}
                    onChange={(event) =>
                      setRouterForm((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    placeholder="Example: Main Router"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                      MikroTik Username <span className="text-rose-500">*</span>
                    </label>

                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="text"
                        value={routerForm.mikrotik_username}
                        onChange={(event) =>
                          setRouterForm((prev) => ({
                            ...prev,
                            mikrotik_username: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pl-10 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                        placeholder="admin"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                      MikroTik Password <span className="text-rose-500">*</span>
                    </label>

                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type={
                          showPasswords["new-router-pass"] ? "text" : "password"
                        }
                        value={routerForm.mikrotik_password}
                        onChange={(event) =>
                          setRouterForm((prev) => ({
                            ...prev,
                            mikrotik_password: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pl-10 pr-12 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                        placeholder="••••••••"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          togglePasswordVisibility("new-router-pass")
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
                        title="Toggle visibility"
                      >
                        {showPasswords["new-router-pass"] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                    DNS
                  </label>

                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      value={routerForm.dns}
                      onChange={(event) =>
                        setRouterForm((prev) => ({
                          ...prev,
                          dns: event.target.value,
                        }))
                      }
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pl-10 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
                      placeholder="1.1.1.1"
                    />
                  </div>

                  <p className="mt-1.5 text-xs font-medium text-slate-400">
                    Sent as <span className="font-mono">dns</span> to the
                    backend.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={closeAddRouterModal}
                  disabled={routerSubmitting}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={routerSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {routerSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Create Router
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetailPage;
